import { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import type { CoralContentInit } from "@voxmedia/duet-framework/coral/types";
import type { CskCoralSectionContainer_contentResource$key } from "src/__generated__/relay/CskCoralSectionContainer_contentResource.graphql";
import { useCskCoralStoryId } from "src/hooks/use-csk-coral-story-id";
import { useCskCoralEmbedKey } from "src/hooks/use-csk-coral-surface-class-name";

import type { CoralStreamEmbed } from "./CskCoralLoader";
import CskCoralSection from "./CskCoralSection";

interface Props {
  contentResource?: CskCoralSectionContainer_contentResource$key;
  onEmbedLoad?: (stream: CoralStreamEmbed) => void;
  containerClassName?: string;
  customScrollerID?: string;
  coralId: string;
}

const CskCoralSectionContainer: FunctionComponent<Props> = ({
  contentResource,
  onEmbedLoad,
  containerClassName,
  customScrollerID,
  coralId,
}) => {
  const wpContentResource = useFragment(
    graphql`
      fragment CskCoralSectionContainer_contentResource on ContentResourceInterface {
        id
        chorusId
        chorusUuid
        globalId
        permalink
        comments {
          enabled
          mode
        }
      }
    `,
    contentResource,
  );

  if (!wpContentResource) {
    throw new Error(
      "CskCoralSectionContainer requires GraphQL data to be passed in.",
    );
  }

  const contentInit: CoralContentInit = {
    chorusId: wpContentResource.chorusId,
    chorusUuid: wpContentResource.chorusUuid,
    globalId: wpContentResource.globalId,
    permalink: wpContentResource.permalink,
    comments: wpContentResource.comments,
    nodeId: wpContentResource.id,
  };

  const storyId = useCskCoralStoryId({
    chorusId: wpContentResource.chorusId,
    chorusUuid: wpContentResource.chorusUuid,
    globalId: wpContentResource.globalId,
    nodeId: wpContentResource.id,
  });
  const embedKey = useCskCoralEmbedKey(storyId);

  return (
    <CskCoralSection
      key={embedKey}
      contentInit={contentInit}
      onEmbedLoad={onEmbedLoad}
      containerClassName={containerClassName}
      customScrollerID={customScrollerID}
      coralId={coralId}
      nodeId={wpContentResource.id}
    />
  );
};

export default CskCoralSectionContainer;
